import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../contexts/CartContext'

export default function Cart() {
  const { cartItems, loading, cartTotal, updateQuantity, removeFromCart, clearCart, isGuest } = useCart()
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Create a unique key for each cart item (product + variant combination)
  const getItemKey = (productId: string, variantId: string | null) =>
    variantId ? `${productId}-${variantId}` : productId

  const handleQuantityChange = async (productId: string, variantId: string | null, newQuantity: number) => {
    const itemKey = getItemKey(productId, variantId)
    setUpdatingItems(prev => new Set(prev).add(itemKey))
    setMessage(null)

    const result = await updateQuantity(productId, variantId, newQuantity)

    if (!result.success) {
      setMessage({ type: 'error', text: result.error || 'Failed to update quantity' })
    }

    setUpdatingItems(prev => {
      const next = new Set(prev)
      next.delete(itemKey)
      return next
    })
  }

  const handleRemoveItem = async (productId: string, variantId: string | null) => {
    const itemKey = getItemKey(productId, variantId)
    setUpdatingItems(prev => new Set(prev).add(itemKey))
    setMessage(null)

    const result = await removeFromCart(productId, variantId)

    if (!result.success) {
      setMessage({ type: 'error', text: result.error || 'Failed to remove item' })
    }

    setUpdatingItems(prev => {
      const next = new Set(prev)
      next.delete(itemKey)
      return next
    })
  }

  const handleClearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your cart?')) return

    setMessage(null)
    const result = await clearCart()

    if (result.success) {
      setMessage({ type: 'success', text: 'Cart cleared successfully' })
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to clear cart' })
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
            <p className="text-gray-500 text-sm">Review and manage items in your cart</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading your cart...</p>
          </div>
        </div>
      </div>
    )
  }

  // Empty cart state
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
            <p className="text-gray-500 text-sm">Review and manage items in your cart</p>
          </div>

          {message && (
            <div className={`mb-5 p-4 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {message.text}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 text-gray-400"
                style={{ width: '24px', height: '24px', maxWidth: '24px', maxHeight: '24px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-gray-500 text-sm mb-6">Start shopping to add items to your cart</p>
            <Link
              to="/products"
              className="inline-flex items-center px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Cart with items
  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
            <p className="text-gray-500 text-sm">{cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in your cart</p>
          </div>
          <button
            onClick={handleClearCart}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Clear Cart
          </button>
        </div>

        {/* Guest Cart Banner */}
        {isGuest && (
          <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0"
                style={{ width: '20px', height: '20px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-blue-700">
                  You're browsing as a guest.{' '}
                  <Link to="/login" className="font-medium underline hover:text-blue-800">
                    Sign in
                  </Link>
                  {' '}or{' '}
                  <Link to="/signup" className="font-medium underline hover:text-blue-800">
                    create an account
                  </Link>
                  {' '}to save your cart and track orders.
                </p>
              </div>
            </div>
          </div>
        )}

        {message && (
          <div className={`mb-5 p-4 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <div className="space-y-4">
          {/* Cart Items */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {cartItems.map((item, index) => {
              const itemKey = getItemKey(item.product_id, item.variant_id)
              // Use variant price/stock if available, otherwise use product price/stock
              const itemPrice = item.variant?.price ?? item.product?.price ?? 0
              const itemStock = item.variant?.stock_quantity ?? item.product?.stock ?? 0

              return (
                <div
                  key={itemKey}
                  className={`p-4 ${index !== cartItems.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                      <div className="w-20 h-20 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                        {item.product?.image_urls && item.product.image_urls.length > 0 ? (
                          <img
                            src={item.product.image_urls[0]}
                            alt={item.product.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1}
                              stroke="currentColor"
                              className="w-8 h-8 text-gray-300"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </Link>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/products/${item.product_id}`}
                        className="text-sm font-semibold text-gray-900 hover:text-purple-600 transition-colors line-clamp-2"
                      >
                        {item.product?.name || 'Unknown Product'}
                      </Link>

                      {/* Variant Info */}
                      {item.variant && (
                        <p className="text-xs text-purple-600 mt-0.5">
                          {item.variant.variant_name}: {item.variant.variant_value}
                        </p>
                      )}

                      <p className="text-lg font-bold text-gray-900 mt-1">
                        ${itemPrice.toFixed(2)}
                      </p>

                      {/* Stock Status */}
                      {itemStock <= 5 && itemStock > 0 && (
                        <p className="text-xs text-amber-600 mt-1">Only {itemStock} left</p>
                      )}
                      {itemStock === 0 && (
                        <p className="text-xs text-red-600 mt-1">Out of stock</p>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.variant_id, item.quantity - 1)}
                          disabled={updatingItems.has(itemKey) || item.quantity <= 1}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
                          </svg>
                        </button>

                        <span className="w-10 text-center font-medium text-gray-900">
                          {updatingItems.has(itemKey) ? (
                            <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></span>
                          ) : (
                            item.quantity
                          )}
                        </span>

                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.variant_id, item.quantity + 1)}
                          disabled={updatingItems.has(itemKey) || item.quantity >= itemStock}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemoveItem(item.product_id, item.variant_id)}
                        disabled={updatingItems.has(itemKey)}
                        className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Item Subtotal */}
                  <div className="mt-3 pt-3 border-t border-gray-50 flex justify-between items-center">
                    <span className="text-xs text-gray-500">Subtotal</span>
                    <span className="text-sm font-semibold text-gray-900">
                      ${(itemPrice * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Cart Summary */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-lg font-bold text-gray-900">${cartTotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-400 mb-4">Shipping and taxes calculated at checkout</p>

            {/* Proceed to Checkout */}
            <Link
              to="/checkout"
              className="block w-full py-3 px-6 bg-purple-600 text-white font-semibold text-sm rounded-xl text-center hover:bg-purple-700 transition-colors"
            >
              Proceed to Checkout
            </Link>

            <Link
              to="/products"
              className="block w-full mt-3 py-3 px-6 bg-white border border-gray-200 text-gray-700 font-medium text-sm rounded-xl text-center hover:bg-gray-50 transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
